import { action, computed, observable } from 'mobx';
import { SecureUser, V3MembershipType } from '../types/user';
import { UserApis } from '../api';
import { TokenListState } from '../../mainv3/types/tokens';
import { AvailableTokenApis } from '../../mainv3/apis/tokens';
import { SubmitState } from '../../commonv3/states/SubmitState';
import { UserPreferenceState } from './UserPreferenceState';
import { PaymentApis } from '../../mainv3/apis/payments';
import { PaymentIndexView } from '../../mainv3/types/payments';
import { updateRemoteResourceV3, RemoteResourceV3 } from '../../commonv3/api/RemoteResourceV3';
import moment, { unitOfTime } from 'moment-timezone';
import { HomeLocationState } from '../../commonv3/states/HomeLocationState';
import { WithdrawalState } from '../../commonv3/states/WithdrawalsState';
import autobind from 'autobind-decorator';
import { MembershipPlanGroupKey } from '../../mainv3/types/membershipPlans';

declare const initialAppState: {
    currentUser: SecureUser | null;
    enabledFlagKeys: string[];
};

export function diffInMonths(dateFrom: string, dateTo: string = null): number {
    const from = moment(dateFrom);
    const to = dateTo ? moment(dateTo) : moment();
    return to.diff(from, 'month');
}

export const FlagKeys = {
    COMMUNITY_SERVICE: 'community_service',
    MULTICREATOR_PH2: 'multicreator_ph2',
};

export class CurrentUserState {
    @observable loadState: SubmitState = new SubmitState();
    @observable currentUser: SecureUser = initialAppState.currentUser;
    @observable availableTokens: TokenListState = new TokenListState();
    @observable homeLocation: HomeLocationState = new HomeLocationState();
    @observable withdrawal: WithdrawalState = new WithdrawalState();
    @observable payments: RemoteResourceV3<PaymentIndexView[]> = RemoteResourceV3.loading();

    getOrThrow() {
        if (!this.currentUser) {
            throw new Error('User must be signed in');
        }
    }

    /**
     * Look up value for the current user.
     * Intended for reliable look up of user values regardless whether the user is signed in or not
     * @param key the key to look up (field of SecureUser)
     * @param defaultValue the default value
     */
    @autobind
    getValue<K extends keyof SecureUser>(key: keyof SecureUser, defaultValue: SecureUser[K]): SecureUser[K] {
        if (!this.currentUser) {
            return defaultValue;
        }
        return this.currentUser[key];
    }

    @action
    async reloadUser(): Promise<SecureUser> {
        await this.loadState.operate(async () => {
            this.currentUser = await UserApis.getCurrentUser();
            if (this.currentUser) {
                await Promise.all([
                    await updateRemoteResourceV3(
                        this.payments,
                        (v) => (this.payments = v),
                        () => PaymentApis.findAll()
                    ),
                    this.availableTokens.init(await AvailableTokenApis.findAll()),
                    this.homeLocation.init(),
                    this.withdrawal.init(),
                ]);
            } else {
                this.availableTokens.init([]);
            }
        });
        return this.currentUser;
    }

    @computed
    get preference(): UserPreferenceState {
        if (!this.currentUser) {
            return new UserPreferenceState({});
        }
        return new UserPreferenceState(this.currentUser.user_preference);
    }

    /**
     * このユーザに対して現在有効なFlagのキーの一覧を返す
     */
    @computed
    get enabledFlagKeys(): string[] {
        if (!initialAppState.enabledFlagKeys) {
            return [];
        }
        return initialAppState.enabledFlagKeys;
    }

    hasEnabledFlagKey(key: string): boolean {
        return this.enabledFlagKeys.includes(key);
    }

    @computed
    get loading(): boolean {
        return this.loadState.submitting;
    }

    @computed
    get userId() {
        return this.currentUser ? this.currentUser.id : null;
    }

    isSignedIn(): boolean {
        return this.currentUser != null;
    }

    isMember(): boolean {
        return this.isSignedIn() && this.currentUser.v2_member;
    }

    isV3Member(): boolean {
        return this.isSignedIn() && this.currentUser.v3_member;
    }

    isMultiCreatorUser(): boolean {
        return (
            this.isSignedIn() &&
            this.currentUser.membership_plan_group_keys.includes(MembershipPlanGroupKey.MULTICREATOR)
        );
    }

    hasEffectiveMembershipToken(): boolean {
        return this.isSignedIn() && this.currentUser.has_effective_membership_token;
    }

    isV3StandardMember(): boolean {
        return this.isSignedIn() && this.currentUser.v3_membership_type == V3MembershipType.STANDARD;
    }

    isV3TrialMember(): boolean {
        return this.isSignedIn() && this.currentUser.v3_membership_type == V3MembershipType.TRIAL;
    }

    isNonMember(): boolean {
        return !this.isMember();
    }

    hasCard(): boolean {
        return this.isSignedIn() && !!this.currentUser.payment_info;
    }

    hasActiveSubscription(): boolean {
        return this.isSignedIn() && this.currentUser.has_active_subscription;
    }

    @computed
    get failedTransactions(): PaymentIndexView[] {
        return this.payments.getOrElse([]).filter((p) => p.status === 'error');
    }

    @computed
    get hasFailedTransactions() {
        return this.failedTransactions.length > 0;
    }

    @computed
    get firstPaymentDateAfterMigration() {
        if (!this.currentUser) {
            return null;
        }
        return this.currentUser.first_payment_date_after_migration;
    }

    isFirstPaymentAfterMigrationWithin(duration: number, unit: unitOfTime.DurationConstructor) {
        if (!this.firstPaymentDateAfterMigration) {
            return false;
        }
        const firstPaymentDate = moment(this.firstPaymentDateAfterMigration);
        return (
            firstPaymentDate.isAfter(moment()) &&
            moment()
                .add(duration, unit)
                .isSameOrAfter(firstPaymentDate)
        );
    }

    @computed
    get isDeactivated(): boolean {
        return this.failedTransactions.find((p) => diffInMonths(p.due_date) >= 1) != null;
    }
}
